'use client';

import * as React from 'react';
import { Map as MapIcon, SlidersHorizontal } from 'lucide-react';

import {
  SelectMenu,
  SelectMenuContent,
  SelectMenuItem,
  SelectMenuTrigger,
  SelectMenuValue,
} from '@/components/atoms/Select';
import { cn } from '@/components/atoms/primitives/utils';
import { MOBILE_FILTER_SHEET_PANEL_ID } from '@/components/organisms/DiscoverFilterRail/mobileFilterSheet.constants';
import { DISCOVER_STICK_BELOW_CHROME_CLASS } from '@/components/templates/DiscoverTemplate/chromeOffsets';
import { DiscoverCountSkeleton } from '@/components/atoms/DiscoverCountSkeleton';
import { formatCount } from '@/lib/discover/formatCount';
import {
  formatDiscoverResultsCountAriaLabel,
  formatDiscoverResultsCountLabel,
} from '@/lib/discover/resultsCountCopy';
import { buildSortOptions } from '@/lib/discover/sortOptions';
import type { DateValue, SortMode, ViewMode } from '@/lib/discover/url-state';

export interface DiscoverResultsBarProps {
  /**
   * Total events matching the active filter set, across all loaded pages.
   * `null` = unresolved — skeleton, never a painted `0` that later becomes
   * non-zero (WEB-786).
   */
  total: number | null;
  /** Active view — `'map'` drives `aria-pressed` on the Map toggle. */
  view: ViewMode;
  /** Active sort mode — drives the selected item in the Select combobox. */
  sort: SortMode;
  /**
   * Fired when the Map toggle is pressed: `'map'` to open the takeover,
   * `'list'` to return from it.
   */
  onViewChange: (view: ViewMode) => void;
  /** Fired when the user picks a different sort option. */
  onSortChange: (sort: SortMode) => void;
  /** Optional class merged onto the outer container. */
  className?: string;
  /**
   * The day strip's slot, between the count cell and the tools. Empty, it is
   * the flexible spacer that right-aligns Filters / Sort / Map.
   */
  stripSlot?: React.ReactNode;
  /**
   * Right-aligned slot for a custom mobile filter control. Prefer the
   * first-class FILTERS pill (`filtersOpen` / `onFiltersOpen`) — this slot
   * remains for tests and stories that inject a stub.
   */
  mobileFilterSlot?: React.ReactNode;
  /**
   * WEB-679 / DPL-28 — Filters trigger in this bar (≤980). Drives
   * `aria-expanded` on the trigger; the sheet itself is mounted by
   * `DiscoverResultsBarConnected`.
   */
  filtersOpen?: boolean;
  /** Opens the mobile filter sheet. When set, the Filters trigger renders. */
  onFiltersOpen?: () => void;
  /** Active-filter count for the pill badge. Omitted / 0 hides the badge. */
  activeFilterCount?: number;
  /** Ref forwarded to the Filters trigger so the sheet can restore focus. */
  filtersTriggerRef?: React.Ref<HTMLButtonElement>;
  /**
   * WEB-667 / DPL-17 — corpus-wide taste-matched total from
   * `meta.tasteMatchedTotal`. The connected wrapper only passes a number
   * when the FRE-917 gate holds (authenticated + real signal). `null` /
   * omitted renders the in-range half alone — never a fabricated zero.
   * A real `0` is a measured zero and MUST render.
   */
  tasteMatchedTotal?: number | null;
  /**
   * WEB-790 / PAR-25 — active `date` URL param, drives the count line's
   * scope noun so it tracks the selected preset instead of a hardcoded
   * "TONIGHT". Omitted / `undefined` is the default "all upcoming" state.
   */
  date?: DateValue;
}

/**
 * Discover context bar (WEB-1074 — v3.1 `.dv-bar`): one sticky 52px row of
 * count · [day-strip slot] · [Filters ≤980] · [Sort >980] · Map.
 *
 * Paint lives in `styles/components/discover-bar.css`; this component maps
 * props to those classes. The `--dv-bar-h` height token is published there
 * for the day sections' `scroll-margin-top` and the strip's jump math.
 *
 * - **Count cell** — the in-range total in display type via the shared
 *   `formatCount`, then the mono label from `resultsCountCopy`: `IN RANGE`,
 *   `IN RANGE TONIGHT` (or the active preset's noun), plus
 *   ` · {n} MATCH YOUR TASTE` only when `tasteMatchedTotal` is a number —
 *   never a fabricated `0` for absent (DPL-21). A full-height hairline closes
 *   the cell.
 * - **Map** — the List/Map tablist is retired: List is the resting state, so
 *   a "List" tab could never be used. One `<button aria-pressed>` bound to
 *   `view === 'map'`; pressing it again (or closing the takeover, which writes
 *   `view=list`) returns it to `false`.
 * - **Sort** — inline at >980. At ≤980 it lives in `MobileFilterDrawer`'s
 *   sheet under Match text; both read `buildSortOptions`, so the two surfaces
 *   cannot disagree about what sorts exist. The trigger is the DS
 *   `SelectMenuTrigger` (Radix listbox, WEB-1020) so the keyboard model and
 *   its focus ring (WEB-829) are unchanged.
 * - **Filters** — ≤980 only; opens the sheet `DiscoverResultsBarConnected`
 *   mounts.
 *
 * Map and Filters paint at 36px and carry `.a-hit-floor`, the coarse-pointer
 * 44px hit guard (FRE-1380), so the touch target never shrinks with the paint.
 *
 * Sticky placement is `.nav-stick-below` via
 * {@link DISCOVER_STICK_BELOW_CHROME_CLASS}: `top: var(--nav-stick-top)` on
 * the `--z-surface-sticky` rung, under the chrome (WEB-1110) — the code's names for the design's
 * `.o-gnav-stick-below` / `--gn-top-h` — at EVERY width (WEB-1081).
 *
 * **≤720 phone treatment (WEB-1081 / FRE-1377)** is CSS only: the count shows
 * the number alone (the sr-only sentence carries `in range` / `tonight`), and
 * Filters and Map become 36px icon-only circles. Their visible labels are
 * visually hidden, not removed, so the accessible names stay `Filters…` (plus
 * the active-filter count) and `Map view`.
 */
export function DiscoverResultsBar({
  total,
  view,
  sort,
  onViewChange,
  onSortChange,
  className,
  stripSlot,
  mobileFilterSlot,
  filtersOpen = false,
  onFiltersOpen,
  activeFilterCount = 0,
  filtersTriggerRef,
  tasteMatchedTotal = null,
  date,
}: DiscoverResultsBarProps): React.ReactElement {
  const sortOptions = React.useMemo(() => buildSortOptions(), []);
  const countsPending = total === null;
  const countInput = { total: total ?? 0, tasteMatchedTotal, date } as const;
  const countAriaLabel = countsPending
    ? 'Loading result count'
    : formatDiscoverResultsCountAriaLabel(countInput);
  const mapOpen = view === 'map';

  return (
    <div
      data-testid="discover-results-bar"
      // WEB-1099: pinned surface chrome — the toast stack opens below it.
      data-ln-chrome=""
      role="region"
      aria-label="Results summary"
      className={cn('dv-bar', DISCOVER_STICK_BELOW_CHROME_CLASS, className)}
    >
      {/*
        WEB-667 / DPL-17 — two-part honest count. Both label variants live in
        the DOM and swap at 720px via CSS, so jsdom can assert both strings and
        Playwright can assert visibility per viewport. AT reads the sr-only full
        wording once; the visual copies are aria-hidden.
      */}
      <p
        className="dv-bar__count"
        data-testid="discover-results-count"
        data-taste-count={
          countsPending
            ? 'pending'
            : tasteMatchedTotal === null
              ? 'absent'
              : String(tasteMatchedTotal)
        }
        aria-live="polite"
        aria-atomic="true"
      >
        {/*
          Accessible name lives in the paragraph's text, not `aria-label`
          (`aria-label` is prohibited on `<p>` and would fail axe
          `aria-prohibited-attr`).
        */}
        <span data-testid="discover-results-count-sr" className="sr-only">
          {countAriaLabel}
        </span>
        {total === null ? (
          // `label={null}`: the sr-only span above already announces
          // "Loading result count" inside this `aria-live` paragraph.
          <DiscoverCountSkeleton data-testid="discover-results-count-skeleton" label={null} />
        ) : (
          <>
            <span
              data-testid="discover-results-count-full"
              aria-hidden="true"
              className="dv-bar__wide"
            >
              <b>{formatCount(total)}</b>
              {formatDiscoverResultsCountLabel({ ...countInput, variant: 'full' })}
            </span>
            <span
              data-testid="discover-results-count-slim"
              aria-hidden="true"
              className="dv-bar__slim"
            >
              <b>{formatCount(total)}</b>
              {/* WEB-1081: hidden at ≤720 (number-only count); the sr-only
                  sentence above is what AT reads at every width. */}
              <span className="dv-bar__lbl">
                {formatDiscoverResultsCountLabel({ ...countInput, variant: 'slim' })}
              </span>
            </span>
          </>
        )}
      </p>

      {/* The day strip mounts here; empty, it is the spacer that right-aligns the tools. */}
      <div className="dv-bar__slot" data-testid="discover-results-bar-strip-slot">
        {stripSlot}
      </div>

      <div className="dv-bar__r">
        {onFiltersOpen ? (
          <div data-testid="discover-results-bar-mobile-filter-slot" className="dv-bar__filters">
            <button
              ref={filtersTriggerRef}
              type="button"
              data-testid="mobile-filter-drawer-trigger"
              onClick={onFiltersOpen}
              aria-haspopup="dialog"
              aria-expanded={Boolean(filtersOpen)}
              aria-controls={MOBILE_FILTER_SHEET_PANEL_ID}
              className="dv-ftrig a-hit-floor"
            >
              <SlidersHorizontal aria-hidden="true" strokeWidth={2} />
              <span className="dv-ftrig__l">Filters</span>
              {activeFilterCount > 0 ? (
                <>
                  <span
                    aria-hidden="true"
                    data-testid="mobile-filter-drawer-trigger-badge"
                    className="dv-ftrig__badge"
                  >
                    {activeFilterCount}
                  </span>
                  <span className="sr-only">
                    {activeFilterCount === 1
                      ? '1 filter active'
                      : `${activeFilterCount} filters active`}
                  </span>
                </>
              ) : null}
            </button>
          </div>
        ) : mobileFilterSlot ? (
          <div data-testid="discover-results-bar-mobile-filter-slot" className="dv-bar__filters">
            {mobileFilterSlot}
          </div>
        ) : null}

        {/*
          WEB-666 (DPL-16) mono label. WEB-1189 C15: the trigger stays
          sentence case ("Soonest"). Mono size and tracking stay.
        */}
        <div data-testid="discover-sort-select-wrap" className="dv-sort">
          <span aria-hidden="true">Sort:</span>
          <SelectMenu value={sort} onValueChange={(value) => onSortChange(value as SortMode)}>
            {/* WEB-1020 ruling: the DS `Button` `ghost` md (40; coarse 44 floor via
                `::before`). Its focus indicator is the DS outline, which is not
                transitioned, so it paints on the frame focus lands (WEB-829). */}
            <SelectMenuTrigger
              data-testid="discover-sort-select"
              aria-label="Sort events by"
              className="font-mono text-micro tracking-[0.04em]"
            >
              <SelectMenuValue placeholder="Sort by" />
            </SelectMenuTrigger>
            <SelectMenuContent>
              {sortOptions.map((option) => (
                <SelectMenuItem key={option.key} value={option.key}>
                  {option.label}
                </SelectMenuItem>
              ))}
            </SelectMenuContent>
          </SelectMenu>
        </div>

        <button
          type="button"
          data-testid="discover-map-toggle"
          aria-pressed={mapOpen}
          aria-label="Map view"
          onClick={() => onViewChange(mapOpen ? 'list' : 'map')}
          className="dv-mapbtn a-hit-floor"
        >
          <MapIcon aria-hidden="true" />
          <span className="dv-mapbtn__l">Map</span>
        </button>
      </div>
    </div>
  );
}
